import styled, { css, keyframes } from 'styled-components';
import { exFont } from '../typography';

// Shared Exams primitives. Colours come only from ExamsScope's tokens.

const TONES = {
  ok: css`
    color: var(--ex-ok-ink);
    background: var(--ex-ok-bg);
  `,
  warn: css`
    color: var(--ex-warn-ink);
    background: var(--ex-warn-bg);
  `,
  bad: css`
    color: var(--ex-bad-ink);
    background: var(--ex-bad-bg);
  `,
  info: css`
    color: var(--ex-info-ink);
    background: var(--ex-info-bg);
  `,
  purple: css`
    color: var(--ex-purple);
    background: var(--ex-purple-100);
  `,
  neutral: css`
    color: var(--ex-ink-2);
    background: var(--ex-surface-2);
  `,
};

export const toneCss = (tone) => TONES[tone] || TONES.neutral;

export const BadgeRoot = styled.span`
  ${exFont(600, 'xs')}
  ${({ $tone }) => toneCss($tone)}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${({ $square }) =>
    $square ? '6px' : 'var(--ex-radius-pill)'};
  white-space: nowrap;
  line-height: 1.4;
`;

export const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

const PILL_VARIANTS = {
  outline: css`
    color: var(--ex-purple);
    background: var(--white);
    border: 1px solid var(--ex-purple);
  `,
  primary: css`
    color: var(--white);
    background: var(--ex-purple);
    border: 1px solid var(--ex-purple);
    &:hover:not(:disabled) {
      box-shadow: var(--ex-shadow-accent);
    }
  `,
  secondary: css`
    color: var(--ex-purple);
    background: var(--ex-purple-100);
    border: 1px solid var(--ex-purple-200);
    &:hover:not(:disabled) {
      background: var(--ex-purple-150);
    }
  `,
  ghost: css`
    color: var(--ex-ink-strong);
    background: transparent;
    border: 1px solid var(--ex-line-strong);
    &:hover:not(:disabled) {
      background: var(--ex-surface-2);
    }
  `,
  danger: css`
    color: var(--white);
    background: var(--ex-bad-ink);
    border: 1px solid var(--ex-bad-ink);
  `,
};

export const PillButton = styled.button`
  ${({ $size }) => exFont(500, $size === 'small' ? 's' : 'm')}
  ${({ $variant }) => PILL_VARIANTS[$variant] || PILL_VARIANTS.primary}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: ${({ $size }) => ($size === 'small' ? '4px 12px' : '8px 16px')};
  border-radius: var(--ex-radius-pill);
  cursor: pointer;
  transition: background 0.1s, box-shadow 0.1s;
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SegmentedRoot = styled.div`
  display: inline-flex;
  padding: 4px;
  gap: 4px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-surface-2);
`;

export const SegmentedOption = styled.button`
  ${exFont(500, 's')}
  padding: 4px 12px;
  border: none;
  border-radius: var(--ex-radius-pill);
  cursor: pointer;
  color: ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-ink-2)')};
  background: ${({ $active }) => ($active ? 'var(--white)' : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? 'var(--ex-shadow-1)' : 'none')};
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Modal scrim (every v6A dialog: rgba(37,34,42,.55), 24px inset).
export const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ $z }) => $z || 1400};
  box-sizing: border-box;
  padding: 24px;
  background: rgba(37, 34, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
`;

// The v6A dialogs come in two sizes ($size on each primitive below):
// - 'warning' (tplA 1181 / 1431 / 1524 / 1189): 28px padding, 700 20px
//   title, 400 14.5px/1.55 body, 22px to the actions, 10px between them,
//   600 15px buttons (outline 12×22, primary 13×26);
// - 'compact' (tplA 731 / 1054 / 1734): 24×26 padding, 700 18px title,
//   400 14px/1.55 body, 20px to the actions, 12px between them, a 600 14px
//   text link and a 40px primary.
// - 'alert' (tplA 1720 re-mark outside the earlier marks): the compact
//   dialog at 480px with an icon beside the title, a 14.5px/1.55 body,
//   14px between blocks and 42px buttons.
// Without $size the card is a plain container (wide cards that lay out
// their own head and body).
const DIALOG_SIZES = {
  warning: {
    card: css`
      box-sizing: border-box;
      width: 460px;
      max-width: 100%;
      padding: 28px;
      gap: 0;
    `,
    title: css`
      ${exFont(700, 20)}
      margin: 0 0 8px;
    `,
    body: css`
      ${exFont(400, 14.5)}
      line-height: 1.55;
      margin: 0 0 22px;
      &:last-child {
        margin-bottom: 0;
      }
    `,
    actions: css`
      gap: 10px;
    `,
  },
  compact: {
    card: css`
      box-sizing: border-box;
      width: 460px;
      max-width: 100%;
      padding: 24px 26px;
      gap: 0;
    `,
    title: css`
      ${exFont(700, 18)}
      margin: 0 0 8px;
    `,
    body: css`
      ${exFont(400, 14)}
      line-height: 1.55;
      margin: 0 0 20px;
      &:last-child {
        margin-bottom: 0;
      }
    `,
    actions: css`
      gap: 12px;
    `,
  },
};

DIALOG_SIZES.alert = {
  card: css`
    box-sizing: border-box;
    width: 480px;
    max-width: 100%;
    padding: 24px 26px;
    gap: 0;
  `,
  title: css`
    ${exFont(700, 18)}
    margin: 0;
  `,
  body: css`
    ${exFont(400, 14.5)}
    line-height: 1.55;
    color: #3d3847;
    margin: 0 0 14px;
    &:last-child {
      margin-bottom: 0;
    }
  `,
  actions: css`
    gap: 10px;
  `,
};

const sized =
  (part) =>
  ({ $size }) =>
    DIALOG_SIZES[$size]?.[part] || '';

export const DialogCard = styled.div`
  width: min(480px, calc(100vw - 32px));
  background: var(--white);
  border-radius: 16px;
  box-shadow: var(--ex-shadow-modal);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  line-height: normal;
  ${sized('card')}
  /* focused as a container by useModalFocus; its controls keep their rings */
  &:focus {
    outline: none;
  }
`;

export const DialogTitle = styled.h2`
  ${exFont(600, 'xl')}
  margin: 0;
  color: var(--ex-ink);
  line-height: normal;
  ${sized('title')}
`;

export const DialogBody = styled.div`
  ${exFont(400, 'm')}
  color: var(--ex-ink-strong);
  line-height: 1.5;
  ${sized('body')}
`;

export const DialogActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: ${({ $size }) => ($size ? '0' : '8px')};
  ${sized('actions')}
`;

// A dialog's icon beside its title (tplA 1721: 40px rounded square, amber).
export const DialogHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 ${({ $size }) => ($size === 'warning' ? '8px' : '14px')};
  > h2 {
    margin: 0;
  }
`;

// The primary choice: 'warning' 600 15px 13×26; 'compact' 600 14px, 40px tall.
const isSmall = ($size) => $size === 'compact' || $size === 'alert';

export const DialogPrimary = styled.button`
  ${({ $size }) => exFont(600, isSmall($size) ? 14 : 15)}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  height: ${({ $size }) =>
    $size === 'alert' ? '42px' : $size === 'compact' ? '40px' : 'auto'};
  padding: ${({ $size }) => (isSmall($size) ? '0 20px' : '13px 26px')};
  border: none;
  border-radius: 999px;
  background: ${({ $danger }) =>
    $danger ? 'var(--ex-bad-ink)' : 'var(--ex-purple)'};
  color: var(--white);
  white-space: nowrap;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

// The outlined secondary choice: 'warning' 600 15px 12×22, 1px (tplA 1434);
// 'compact' 600 14px, 42px tall, 1.5px (tplA 1724).
export const DialogOutline = styled.button`
  ${({ $size }) => exFont(600, isSmall($size) ? 14 : 15)}
  box-sizing: border-box;
  height: ${({ $size }) => (isSmall($size) ? '42px' : 'auto')};
  padding: ${({ $size }) => (isSmall($size) ? '0 18px' : '12px 22px')};
  border: ${({ $size }) => (isSmall($size) ? '1.5px' : '1px')} solid
    var(--ex-purple);
  border-radius: 999px;
  background: var(--white);
  color: var(--ex-purple);
  white-space: nowrap;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--ex-selected-tint);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

// A text-link choice, 600 14px: purple ("Calibrate anyway", "Continue
// anyway") or grey for Cancel / Re-check ($muted).
export const DialogLink = styled.button`
  ${exFont(600, 14)}
  padding: 0;
  border: none;
  background: none;
  color: ${({ $muted }) => ($muted ? 'var(--ex-ink-2)' : 'var(--ex-purple)')};
  white-space: nowrap;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const OverlayBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ $z }) => ($z || 1300) - 1};
  background: rgba(37, 34, 42, 0.55);
`;

export const OverlayRoot = styled.div`
  position: fixed;
  inset: ${({ $inset }) =>
    $inset ? '24px max(24px, calc((100vw - 1080px) / 2))' : '0'};
  z-index: ${({ $z }) => $z || 1300};
  border-radius: ${({ $inset }) => ($inset ? '16px' : '0')};
  max-height: ${({ $inset }) => ($inset ? '760px' : 'none')};
  margin: ${({ $inset }) => ($inset ? 'auto' : '0')};
  background: var(--white);
  display: flex;
  flex-direction: column;
  overflow: auto;
  line-height: normal;
  /* focused as a container by useModalFocus; its controls keep their rings */
  &:focus {
    outline: none;
  }
`;

export const OverlayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 22px;
  border-bottom: 1px solid var(--ex-line);
  > button {
    width: 20px;
    height: 20px;
    padding: 0;
  }
`;

export const OverlayHeading = styled.div`
  flex: 1;
`;

export const OverlaySubtitle = styled.p`
  ${exFont(400, 's')}
  margin: 2px 0 0;
  color: var(--ex-ink-2);
`;

export const OverlayTitle = styled.h2`
  ${exFont(700, 19)}
  margin: 0;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ex-ink-strong);
  cursor: pointer;
  &:hover {
    background: var(--ex-surface-2);
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const ToastCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  animation: ${fadeIn} 0.15s ease-out;
`;

// App.jsx's ToastContainer is theme="dark" (#121212): light text, as in the
// design's toasts (white 500 14px on #25222a, tplA 133 / 1714).
export const ToastTitle = styled.p`
  ${exFont(500, 14)}
  margin: 0;
  color: var(--white);
`;

export const ToastBody = styled.p`
  ${exFont(400, 12.5)}
  margin: 0;
  color: #d9d6de;
  line-height: 1.45;
`;

export const InlineErrorRoot = styled.div`
  ${exFont(400, 's')}
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  border-radius: var(--ex-radius-item);
  color: var(--ex-bad-ink);
  background: var(--ex-bad-bg);
`;

export const InlineErrorAction = styled.button`
  ${exFont(600, 's')}
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;
